import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  ArrowUpRight,
  ShieldCheck,
  Boxes,
  FileCode2,
  GaugeCircle,
  Sparkles,
  Check,
} from "lucide-react";
import { CloudIcon, LangIcon } from "@/components/brand-icons";

const plans = [
  {
    id: "free",
    name: "Free",
    price: 0,
    seats: "Explore the app",
    free: true,
    cta: "Start exploring",
    features: ["Read-only governance rules", "Browse the resource catalog", "Preview generated IaC", "No Git push, no CI checks"],
  },
  {
    id: "basic",
    name: "Basic",
    price: 250,
    seats: "1 user",
    cta: "Get started",
    features: ["Unlimited projects", "Azure, AWS & GCP", "Terraform & Bicep", "Git connectors & CI compliance"],
  },
  {
    id: "standard",
    name: "Standard",
    price: 1000,
    seats: "3 users",
    popular: true,
    cta: "Get started",
    features: ["Everything in Basic", "Project sharing between teammates", "Multi-environment budgets", "Policy-as-Code export"],
  },
  {
    id: "enterprise",
    name: "Enterprise",
    price: 2000,
    seats: "10 users",
    cta: "Get started",
    features: ["Everything in Standard", "Version watch & upgrade alerts", "Priority support", "SSO (coming soon)"],
  },
];

const features = [
  {
    icon: ShieldCheck,
    title: "Governance as one source of truth",
    body: "Define your naming, tagging, regional and SKU policies once. Every generated project inherits them automatically.",
  },
  {
    icon: Boxes,
    title: "Curated resource catalog",
    body: "Only expose the Azure and AWS resources your platform team has approved. No ad hoc IaC drift.",
  },
  {
    icon: FileCode2,
    title: "Real IaC output",
    body: "Ship production-ready Terraform and Bicep projects with best practices baked in. Download the ZIP, commit, deploy.",
  },
  {
    icon: GaugeCircle,
    title: "Live compliance",
    body: "See in one glance which projects are still aligned with your policies, and which need to be updated.",
  },
];

const codeSample = `resource "azurerm_key_vault" "main" {
  name                        = "cor-web-api-kv"
  location                    = azurerm_resource_group.main.location
  resource_group_name         = azurerm_resource_group.main.name
  tenant_id                   = data.azurerm_client_config.current.tenant_id
  sku_name                    = "standard"
  purge_protection_enabled    = true
  enable_rbac_authorization   = true
  tags                        = var.tags
}`;

// Per-tier colour tokens (popular = dark card, free = muted, default = standard).
const PLAN_TONES = {
  popular: { title: "text-white", seats: "text-slate-300", check: "text-emerald-400", feature: "text-slate-200", cta: "bg-white text-slate-900 hover:bg-slate-100" },
  free: { title: "text-slate-500", seats: "text-slate-400", check: "text-slate-300", feature: "text-slate-400", cta: "border border-slate-300 bg-white text-slate-500 hover:bg-slate-50" },
  default: { title: "text-slate-900", seats: "text-slate-600", check: "text-slate-900", feature: "text-slate-600", cta: "bg-slate-900 text-white hover:bg-slate-800" },
};
const planTone = (p) => {
  if (p.popular) return PLAN_TONES.popular;
  return p.free ? PLAN_TONES.free : PLAN_TONES.default;
};

function highlight(code) {
  const tokens = [];
  const push = (start, end, cls) => tokens.push({ start, end, cls });
  const strRe = /"[^"]*"/g;
  let m;
  while ((m = strRe.exec(code))) push(m.index, m.index + m[0].length, "st");
  const kwRe = /\b(resource|true|false)\b/g;
  while ((m = kwRe.exec(code))) push(m.index, m.index + m[0].length, m[0] === "true" || m[0] === "false" ? "nb" : "kw");
  tokens.sort((a, b) => a.start - b.start);
  const out = [];
  let i = 0;
  for (const t of tokens) {
    if (t.start < i) continue;
    if (t.start > i) out.push(code.slice(i, t.start));
    out.push(<span key={`${t.cls}-${t.start}`} className={t.cls}>{code.slice(t.start, t.end)}</span>);
    i = t.end;
  }
  if (i < code.length) out.push(code.slice(i));
  return out;
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="landing-page">
      {/* Nav */}
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
            <div className="grid h-7 w-7 place-items-center rounded-md bg-slate-900 text-white">
              <span className="font-display text-sm font-bold">T</span>
            </div>
            <span className="font-display text-lg font-semibold tracking-tight">
              Toastern
            </span>
            <Badge variant="secondary" className="ml-2 hidden bg-slate-100 text-[10px] uppercase tracking-wider text-slate-600 sm:inline-flex">
              Cloud Governance
            </Badge>
          </Link>
          <nav className="hidden items-center gap-8 text-sm text-slate-600 md:flex">
            <a href="#features" className="hover:text-slate-900" data-testid="nav-features">Product</a>
            <a href="#how" className="hover:text-slate-900" data-testid="nav-how">How it works</a>
            <a href="#pricing" className="hover:text-slate-900" data-testid="nav-pricing">Pricing</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="nav-login">
              <Button variant="ghost" className="text-slate-700">Sign in</Button>
            </Link>
            <Link to="/register" data-testid="nav-register">
              <Button className="bg-slate-900 text-white hover:bg-slate-800">
                Start free
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="absolute inset-0 bg-dots opacity-60" aria-hidden />
        <div className="relative mx-auto grid max-w-7xl grid-cols-12 gap-8 px-6 py-24 md:py-32">
          <div className="col-span-12 lg:col-span-7">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs uppercase tracking-widest text-slate-600">
              <Sparkles className="h-3 w-3" />
              Governance-first IaC
            </div>
            <h1 className="font-display text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              Ship cloud projects that already{" "}
              <span className="text-slate-400">respect your rules.</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-slate-600">
              Toastern lets IT teams codify their cloud governance once and generate
              conformant Terraform &amp; Bicep projects on demand. Azure, AWS &mdash; one
              policy, one catalog, one workflow.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link to="/register" data-testid="hero-register">
                <Button className="h-11 rounded-md bg-slate-900 px-6 text-white hover:bg-slate-800">
                  Start free
                  <ArrowUpRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/login" data-testid="hero-demo">
                <Button
                  variant="outline"
                  className="h-11 rounded-md border-slate-200 bg-white px-6 text-slate-800 hover:bg-slate-50"
                >
                  Sign in
                </Button>
              </Link>
            </div>
            <div className="mt-14 flex flex-wrap items-center gap-6 text-xs uppercase tracking-widest text-slate-500">
              <span className="flex items-center gap-2"><CloudIcon cloud="azure" className="h-4 w-4" /> Azure</span>
              <span className="flex items-center gap-2"><CloudIcon cloud="aws" className="h-4 w-4" /> AWS</span>
              <span className="flex items-center gap-2"><CloudIcon cloud="gcp" className="h-4 w-4" /> GCP</span>
              <span className="flex items-center gap-2"><LangIcon language="terraform" className="h-4 w-4" /> Terraform</span>
              <span className="flex items-center gap-2"><LangIcon language="bicep" className="h-4 w-4" /> Bicep</span>
            </div>
          </div>
          <div className="col-span-12 lg:col-span-5">
            <div className="terminal rounded-lg border border-slate-200 shadow-sm">
              <div className="flex items-center gap-2 border-b border-[rgba(255,255,255,0.12)] px-4 py-3">
                <span className="h-2 w-2 rounded-full bg-red-400/70" />
                <span className="h-2 w-2 rounded-full bg-amber-400/70" />
                <span className="h-2 w-2 rounded-full bg-emerald-400/70" />
                <span className="ml-3 font-mono-tight text-[11px] text-slate-500">
                  cor-web-api / main.tf
                </span>
              </div>
              <pre className="terminal overflow-x-auto rounded-b-lg p-5">
                <code>
                  <span className="cm"># Auto-generated by Toastern · governance v14</span>
                  {"\n"}
                  <span>{highlight(codeSample)}</span>
                </code>
              </pre>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="border-b border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-24">
          <div className="mb-16 max-w-2xl">
            <p className="mb-3 font-mono-tight text-xs uppercase tracking-widest text-slate-500">
              // The product
            </p>
            <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">
              One governance layer for every cloud project you spin up.
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-0 border-t border-l border-slate-200 md:grid-cols-2">
            {features.map((f) => (
              <div
                key={f.title}
                className="group border-b border-r border-slate-200 bg-white p-10 transition-colors hover:bg-slate-50"
                data-testid={`feature-${f.title.split(" ")[0].toLowerCase()}`}
              >
                <f.icon className="h-6 w-6 text-slate-900" strokeWidth={1.5} />
                <h3 className="mt-6 font-display text-lg font-semibold">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 py-24">
          <div className="mb-14">
            <p className="mb-3 font-mono-tight text-xs uppercase tracking-widest text-slate-500">
              // Workflow
            </p>
            <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">
              Three steps from policy to production-grade IaC.
            </h2>
          </div>
          <div className="grid gap-8 md:grid-cols-3">
            {[
              { n: "01", t: "Define", d: "Set your naming, region, tag and SKU rules in the governance panel." },
              { n: "02", t: "Curate", d: "Pick the Azure and AWS resources you allow in your catalog." },
              { n: "03", t: "Generate", d: "Non-experts scaffold conformant Terraform and Bicep projects in seconds." },
            ].map((s) => (
              <div key={s.n} className="border-l border-slate-200 pl-6" data-testid={`step-${s.n}`}>
                <div className="font-mono-tight text-xs uppercase tracking-widest text-slate-500">{s.n}</div>
                <h3 className="mt-3 font-display text-xl font-semibold">{s.t}</h3>
                <p className="mt-2 text-sm text-slate-600">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="border-b border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-24">
          <div className="mb-14 max-w-2xl">
            <p className="mb-3 font-mono-tight text-xs uppercase tracking-widest text-slate-500">
              // Pricing
            </p>
            <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">
              Simple seats. Governance for the whole platform team.
            </h2>
            <p className="mt-4 text-slate-600">
              Every paid plan includes unlimited projects, all clouds, Terraform &amp; Bicep, Git connectors and CI compliance checks.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-0 border-t border-l border-slate-200 md:grid-cols-2 xl:grid-cols-4" data-testid="pricing-grid">
            {plans.map((p) => (
              <div
                key={p.id}
                data-testid={`plan-${p.id}`}
                className={cn(
                  "relative flex flex-col border-b border-r border-slate-200 p-8 transition-colors",
                  p.free ? "bg-slate-100/70 text-slate-400" : "bg-white hover:bg-slate-50",
                  p.popular && "bg-slate-950 text-white hover:bg-slate-950",
                )}
              >
                {p.popular && (
                  <span className="absolute right-6 top-6 rounded-full border border-slate-700 bg-slate-900 px-2.5 py-0.5 font-mono-tight text-[10px] uppercase tracking-widest text-slate-200" data-testid="plan-popular-badge">
                    Most popular
                  </span>
                )}
                {p.free && (
                  <span className="absolute right-6 top-6 rounded-full border border-slate-300 bg-white px-2.5 py-0.5 font-mono-tight text-[10px] uppercase tracking-widest text-slate-400">
                    Explore only
                  </span>
                )}
                <h3 className={cn("font-display text-lg font-semibold", planTone(p).title)}>{p.name}</h3>
                <div className="mt-6 flex items-baseline gap-1">
                  <span className={cn("font-display text-4xl font-semibold tracking-tight", p.free && "text-slate-400")} data-testid={`plan-price-${p.id}`}>
                    {p.price === 0 ? "$0" : `$${p.price.toLocaleString()}`}
                  </span>
                  <span className={cn("font-mono-tight text-xs uppercase tracking-widest", p.popular ? "text-slate-400" : "text-slate-500")}>/ month</span>
                </div>
                <p className={cn("mt-2 font-mono-tight text-xs uppercase tracking-widest", planTone(p).seats)} data-testid={`plan-seats-${p.id}`}>
                  {p.seats}
                </p>
                <ul className="mt-8 flex-1 space-y-3 text-sm">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check className={cn("mt-0.5 h-4 w-4 shrink-0", planTone(p).check)} strokeWidth={2} />
                      <span className={planTone(p).feature}>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/register" className="mt-10" data-testid={`plan-cta-${p.id}`}>
                  <Button
                    className={cn("h-11 w-full rounded-md", planTone(p).cta)}
                    variant={p.free ? "outline" : "default"}
                  >
                    {p.cta}
                    <ArrowUpRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            ))}
          </div>
          <p className="mt-6 font-mono-tight text-xs text-slate-500">
            Prices in USD, billed monthly. Need more than 10 seats or SSO? <Link to="/register" className="underline underline-offset-4 hover:text-slate-900">Talk to us</Link>.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-slate-200 bg-slate-950 text-white">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 px-6 py-20 md:flex-row md:items-center">
          <div>
            <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">
              Stop rewriting the same rules in every repo.
            </h2>
            <p className="mt-3 max-w-xl text-slate-400">
              Start free. Bring your team in when your policy is ready.
            </p>
          </div>
          <Link to="/register" data-testid="cta-register">
            <Button className="h-11 rounded-md bg-white px-6 text-slate-900 hover:bg-slate-100">
              Create your workspace
              <ArrowUpRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="mx-auto max-w-7xl px-6 py-10 text-xs text-slate-500">
        © {new Date().getFullYear()} Toastern · Governance for cloud teams
      </footer>
    </div>
  );
}
